import { useState } from 'react'

import EditIcon from '@mui/icons-material/Edit'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import Chip from '@mui/material/Chip'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { formatPlanning } from '@util/bijlage-template-util'

import { useAuthenticatedUser } from '../../auth/hooks/useAuthenticatedUser'
import { StyledIconButton } from '../../theme/components/StyledIconButton'
import type { BijlageTemplate as BijlageTemplateType } from '../types/BijlageTemplate'
import { formatAssignedTo } from '../util'

import { TemplateOption } from './TemplateOption'
import { BijlageTemplateConditional } from './dialogs/BijlageTemplateConditional'
import { EditBijlageTemplate } from './dialogs/EditBijlageTemplate'

interface Props {
  bijlageTemplate: BijlageTemplateType
  templateSet?: BijlageTemplateType[]
}

export const BijlageTemplate = ({ bijlageTemplate, templateSet }: Props) => {
  const {
    documentsoort,
    sortOrder,
    waitFor,
    requiresReview,
    reference,
    herplanDiff,
    perDiscipline,
    auto,
    assignedTo,
    conditionalAssignedTo,
    werklijst,
  } = bijlageTemplate

  const [open, setOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const user = useAuthenticatedUser()

  return (
    <TableRow hover>
      <TableCell>
        {sortOrder && <Chip label={sortOrder} variant="outlined" />}
      </TableCell>

      <TableCell>{documentsoort}</TableCell>

      <TableCell>
        {/* planning bestaat alleen op project templates */}
        {bijlageTemplate.scope === 'project' && (
          <>
            {formatPlanning(herplanDiff, reference, waitFor)}
            <br />
            {waitFor && (
              <Typography variant="caption">
                Wacht op afronden: {bijlageTemplate.waitFor}
              </Typography>
            )}
          </>
        )}
      </TableCell>

      <TableCell>
        {assignedTo ? formatAssignedTo(assignedTo) : 'Geen'}
      </TableCell>

      <TableCell>
        <TemplateOption label="Automatisch" option={auto} />
        <TemplateOption label="Per discipline" option={perDiscipline} />
        <TemplateOption label="Beoordeling verplicht" option={requiresReview} />
        {requiresReview && bijlageTemplate.canBeContinued && (
          <TemplateOption
            label="Doorwerken"
            option={bijlageTemplate.canBeContinued}
          />
        )}
        <TemplateOption label="Werklijst" option={werklijst} />
      </TableCell>

      <TableCell>
        {conditionalAssignedTo && (
          <StyledIconButton
            title="Toon uitzondering"
            icon={<LightbulbIcon />}
            onClick={() => setOpen(true)}
          />
        )}
      </TableCell>
      <TableCell>
        {user.canUpdateBijlageTemplate &&
          bijlageTemplate.scope !== 'algemeen' && (
            <StyledIconButton
              title="Bewerk template"
              icon={<EditIcon />}
              onClick={() => setEditOpen(true)}
            />
          )}
      </TableCell>

      {/* andere templates dan project hebben (nog) geen conditional */}
      {open && bijlageTemplate.scope === 'project' && (
        <BijlageTemplateConditional
          bijlageTemplate={bijlageTemplate}
          onClose={() => setOpen(false)}
        />
      )}

      {editOpen && (
        <EditBijlageTemplate
          bijlageTemplate={bijlageTemplate}
          templateSet={templateSet}
          onClose={() => setEditOpen(false)}
        />
      )}
    </TableRow>
  )
}
